'use client';

import { Button, Checkbox, Divider, Modal } from '@common/components';
import { useToast } from '@common/hooks';
import { cn } from '@core/utils';
import type { ProductOptionListResponse } from '@generated/auth/types.gen';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AuthQueryOptions } from '@/api';
import { BREAKPOINTS, ROUTES, WEL_MARKET_ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAuthStore } from '@/stores/AuthStore';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
import { useCartCheckState } from '../_hooks/useCartCheckState';
import { useCartOptimistic } from '../_hooks/useCartOptimistic';
import { useCartOrder } from '../_hooks/useCartOrder';
import { useMarketCart } from '../_hooks/useMarketCart';
import { calcCheckedCount, calcShopCheckedCount, calcShopDeliveryFee, calcShopDiscount, calcShopTotal, isProductEffectivelyChecked, optionKey } from '../_utils/cartCalculations';
import { CartBottomBar } from './CartBottomBar';
import { CartEmptyState } from './CartEmptyState';
import { CartOptionChangeModal } from './CartOptionChangeModal';
import type { CartOption, CartProduct } from './CartShopGroup';
import { CartShopGroup } from './CartShopGroup';
import { CartSidebar } from './CartSidebar';
import { CartSummarySection } from './CartSummarySection';
interface EditingProduct {
  mallVendorId: string;
  product: CartProduct;
}
export const CartContent = () => {
  const router = useCustomRouter();
  const {
    toast
  } = useToast();
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const isLogin = useAuthStore(state => state.isLogin);
  const {
    shops: serverShops,
    isLoading,
    addItem,
    updateQuantity,
    deleteItems
  } = useMarketCart();
  useEffect(() => {
    if (isLogin === false) {
      router.replace(ROUTES.LOGIN.withSearchParams({
        searchParams: {
          redirect: WEL_MARKET_ROUTES.CART.pathname
        }
      }));
    }
  }, [isLogin, router]);
  const hasTrackedViewCart = useRef(false);
  useEffect(() => {
    if (hasTrackedViewCart.current || isLoading) return;
    hasTrackedViewCart.current = true;
    trackGtmEvent(GTM_EVENT_TYPE.VIEW_CART, {
      currency: 'KRW',
      value: serverShops.reduce((sum, shop) => sum + calcShopTotal(shop), 0),
      items: serverShops.flatMap(shop_0 => shop_0.products.flatMap(shopProduct => shopProduct.options.map(option => ({
        item_type: '웰마켙상품' as const,
        price: option.price,
        item_name: shopProduct.productName,
        quantity: option.quantity,
        item_id: shopProduct.mallProductId,
        product_uuid: shopProduct.mallProductId,
        product_brand_name: shop_0.vendorName
      }))))
    });
  }, [isLoading, serverShops]);
  const deleteItemsWithTracking = (cartItemOptionIds: string[]) => {
    const removedOptionIdSet = new Set(cartItemOptionIds);
    const removedItems = shops.flatMap(shop_1 => shop_1.products.flatMap(shopProduct_0 => shopProduct_0.options.filter(option_0 => removedOptionIdSet.has(option_0.cartItemOptionId)).map(option_1 => ({
      item_type: '웰마켙상품' as const,
      price: option_1.price,
      item_name: shopProduct_0.productName,
      quantity: option_1.quantity,
      item_id: shopProduct_0.mallProductId,
      product_uuid: shopProduct_0.mallProductId,
      product_brand_name: shop_1.vendorName
    }))));
    if (removedItems.length > 0) {
      trackGtmEvent(GTM_EVENT_TYPE.REMOVE_FROM_CART, {
        currency: 'KRW',
        value: removedItems.reduce((sum_0, item) => sum_0 + item.price * item.quantity, 0),
        items: removedItems
      });
    }
    return deleteItems(cartItemOptionIds);
  };
  const {
    shops,
    setShops,
    allChecked,
    handleAllCheck,
    handleShopCheck,
    handleProductCheck
  } = useCartCheckState(serverShops);
  const {
    handleQuantityChange,
    removeTarget,
    setRemoveTarget,
    handleRemoveProduct,
    handleRemoveOption,
    confirmRemoveProduct
  } = useCartOptimistic({
    shops,
    setShops,
    updateQuantity,
    deleteItems: deleteItemsWithTracking
  });
  const {
    createBeforeOrderAndNavigate,
    isCreatingOrder
  } = useCartOrder();
  const [editingProduct, setEditingProduct] = useState<EditingProduct | null>(null);
  const handleOptionChange = useCallback((mallVendorId: string, product: CartProduct) => {
    setEditingProduct({
      mallVendorId,
      product
    });
  }, []);
  const handleOptionConfirm = useCallback(async (updatedOptions: CartOption[]) => {
    if (!editingProduct) return;
    const {
      mallVendorId: mallVendorId_0,
      product: product_0
    } = editingProduct;
    const originalOptions = product_0.options.filter(o => !o.soldOutYN);
    const origByKey = new Map(originalOptions.map(o_0 => [optionKey(o_0), o_0]));
    const updatedByKey = new Map(updatedOptions.map(o_1 => [optionKey(o_1), o_1]));
    const kept = originalOptions.filter(o_2 => updatedByKey.has(optionKey(o_2)));
    const removed = originalOptions.filter(o_3 => !updatedByKey.has(optionKey(o_3)));
    const added = updatedOptions.filter(o_4 => !origByKey.has(optionKey(o_4)) && o_4.productOptionItemIds.length > 0);
    const skippedAdded = updatedOptions.filter(o_5 => !origByKey.has(optionKey(o_5)) && o_5.productOptionItemIds.length === 0);
    if (skippedAdded.length > 0) {
      console.warn('[handleOptionConfirm] skipped added options with empty productOptionItemIds (COMBINATION type not supported)', skippedAdded);
    }
    try {
      // Run kept quantity updates, removes, and adds in parallel
      await Promise.all([...kept.flatMap(orig => {
        const updated = updatedByKey.get(optionKey(orig));
        if (updated && updated.quantity !== orig.quantity) {
          return [updateQuantity(orig.cartItemOptionId, updated.quantity)];
        }
        return [];
      }), removed.length > 0 ? deleteItems(removed.map(o_6 => o_6.cartItemOptionId)) : Promise.resolve(), ...added.map(o_7 => addItem({
        mallProductId: product_0.mallProductId,
        mallVendorId: mallVendorId_0,
        options: [{
          productOptionItemId: o_7.productOptionItemIds[0],
          quantity: o_7.quantity
        }]
      }))]);
      toast({
        message: '옵션이 변경되었습니다'
      });
    } catch {
      toast({
        message: '옵션 변경에 실패했습니다.',
        type: 'fail'
      });
    }
  }, [editingProduct, addItem, deleteItems, updateQuantity, toast]);
  const {
    data: productOptionsData
  } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/local-market/cart-items/product-options/{productId}']({
      path: {
        productId: editingProduct?.product.mallProductId ?? ''
      }
    }),
    enabled: !!editingProduct
  });
  const apiOptionsToCartOptions = (data: ProductOptionListResponse | undefined, basePrice: number): CartOption[] => {
    if (!data) return [];
    if (data.optionType === 'COMBINATION' && data.optionCombinations) {
      return data.optionCombinations.map(combo => ({
        cartItemOptionId: combo.combinationId ?? '',
        productOptionItemIds: [],
        optionName: combo.combinationName ?? '',
        quantity: 0,
        price: basePrice + (combo.extraPrice ?? 0),
        extraPrice: combo.extraPrice ?? 0,
        soldOutYN: combo.soldOut ?? false
      }));
    }
    if (data.optionGroups) {
      return data.optionGroups.flatMap(group => (group.items ?? []).map(item_0 => ({
        cartItemOptionId: item_0.optionItemId ?? '',
        productOptionItemIds: [item_0.optionItemId ?? ''],
        optionName: `${group.optionGroupName}: ${item_0.itemName}`,
        quantity: 0,
        price: basePrice + (item_0.extraPrice ?? 0),
        extraPrice: item_0.extraPrice ?? 0,
        soldOutYN: item_0.soldOut ?? false
      })));
    }
    return [];
  };
  const freshOptions = (() => {
    const raw = apiOptionsToCartOptions(productOptionsData?.context, editingProduct?.product.currentPrice ?? 0);
    if (raw.length === 0) return raw;

    // Restore quantities from existing cart options (INDEPENDENT only)
    const existingOptions = editingProduct?.product.options ?? [];
    const existingByKey = new Map(existingOptions.map(o_8 => [optionKey(o_8), o_8.quantity]));
    return raw.map(o_9 => {
      const key = optionKey(o_9);
      const existingQty = existingByKey.get(key);
      return existingQty !== undefined ? {
        ...o_9,
        quantity: existingQty
      } : o_9;
    });
  })();
  const allOptions = freshOptions.length > 0 ? freshOptions : editingProduct?.product.options ?? [];
  const totalProductAmount = useMemo(() => shops.reduce((sum_1, shop_2) => sum_1 + calcShopTotal(shop_2), 0), [shops]);
  const totalDeliveryFee = useMemo(() => shops.reduce((sum_2, shop_3) => sum_2 + calcShopDeliveryFee(shop_3), 0), [shops]);
  const totalDiscountAmount = useMemo(() => shops.reduce(sum_3 => sum_3 + calcShopDiscount(), 0), [shops]);
  const totalFinalAmount = totalProductAmount + totalDeliveryFee - totalDiscountAmount;
  const totalCheckedCount = useMemo(() => calcCheckedCount(shops), [shops]);
  const allCheckedProducts = useMemo(() => shops.flatMap(s => s.products.filter(p => p.checked)), [shops]);
  const totalPaymentDisabled = isCreatingOrder || allCheckedProducts.length > 0 && allCheckedProducts.every(p_0 => p_0.soldOutYN || p_0.options.some(o_10 => o_10.soldOutYN));
  const handleProductOrder = (mallProductId: string) => {
    const product_1 = shops.flatMap(s_0 => s_0.products).find(p_1 => p_1.mallProductId === mallProductId);
    if (!product_1) return;
    const cartItemIds = product_1.options.filter(o_11 => !o_11.soldOutYN).map(o_12 => o_12.cartItemOptionId);
    createBeforeOrderAndNavigate(cartItemIds);
  };
  const handleOrder = () => {
    const cartItemIds_0 = shops.flatMap(shop_4 => shop_4.products.filter(p_2 => isProductEffectivelyChecked(p_2))).flatMap(p_3 => p_3.options.filter(o_13 => !o_13.soldOutYN).map(o_14 => o_14.cartItemOptionId));
    createBeforeOrderAndNavigate(cartItemIds_0);
  };
  if (isLoading) return null;
  if (shops.length === 0) {
    return <CartEmptyState />;
  }
  return <>
      <div className={cn('grid grid-cols-[1fr_480px] gap-x-60 gap-y-0 px-40 pb-20 pt-16 content-container', 'mobile-tablet:grid-cols-1 mobile-tablet:px-0 mobile-tablet:pt-0')}>
        {/* Left Column */}
        <section className="order-1 flex flex-col">
          {/* Page Title (desktop only) */}
          <h1 className="py-36 font-700 text-Gray-900 typo-h5 mobile-tablet:hidden">
            장바구니
          </h1>

          {/* Select All */}
          <div className="flex items-center mobile-tablet:mt-12 mobile-tablet:px-16">
            <Checkbox className="flex items-center gap-8 p-0 [&_button[data-state=checked]:not(:disabled)]:bg-O-400" checked={allChecked} onCheckedChange={handleAllCheck}>
              <span className="font-700 text-Gray-900 typo-body1 mobile-tablet:typo-body3">
                전체 선택
              </span>
            </Checkbox>
          </div>

          <Divider className="mt-12 hidden h-8 w-full bg-Gray-100 mobile-tablet:block" />

          {/* Shop Groups */}
          <div className="flex flex-col gap-56 pt-36 mobile-tablet:gap-32 mobile-tablet:pt-32">
            {shops.map((shop_5, index) => {
            const productAmount = calcShopTotal(shop_5);
            const deliveryFee = calcShopDeliveryFee(shop_5);
            const discount = calcShopDiscount();
            const checkedProducts = shop_5.products.filter(p_4 => p_4.checked);
            const onlyHasSoldOut = checkedProducts.length > 0 && checkedProducts.every(p_5 => p_5.soldOutYN || p_5.options.some(o_15 => o_15.soldOutYN));
            return <section key={shop_5.mallVendorId} aria-label={shop_5.vendorName}>
                  <CartShopGroup shop={shop_5} onShopCheck={handleShopCheck} onProductCheck={handleProductCheck} onRemoveProduct={handleRemoveProduct} onRemoveOption={handleRemoveOption} onQuantityChange={handleQuantityChange} onOptionChange={handleOptionChange} onOrder={handleProductOrder} />

                  {/* Per-shop Summary */}
                  <CartSummarySection productAmount={productAmount} deliveryFee={deliveryFee} discountAmount={discount} finalAmount={productAmount + deliveryFee - discount} totalCount={calcShopCheckedCount(shop_5)} paymentDisabled={onlyHasSoldOut || isCreatingOrder} onOrder={() => {
                const cartItemIds_1 = shop_5.products.filter(p_6 => isProductEffectivelyChecked(p_6)).flatMap(p_7 => p_7.options.filter(o_16 => !o_16.soldOutYN).map(o_17 => o_17.cartItemOptionId));
                createBeforeOrderAndNavigate(cartItemIds_1);
              }} />
                  {index < shops.length - 1 && <Divider className="mt-56 w-full bg-Gray-300  mobile-tablet:mt-28 mobile-tablet:h-8 mobile-tablet:bg-Gray-100" />}
                </section>;
          })}
          </div>
        </section>

        {/* Right Sidebar (desktop only) */}
        <CartSidebar productAmount={totalProductAmount} deliveryFee={totalDeliveryFee} discountAmount={totalDiscountAmount} finalAmount={totalFinalAmount} totalCount={totalCheckedCount} paymentDisabled={totalPaymentDisabled} onOrder={handleOrder} />
      </div>

      {/* Mobile Bottom Bar */}
      <CartBottomBar discountAmount={totalDiscountAmount} finalAmount={totalFinalAmount} totalCount={totalCheckedCount} paymentDisabled={totalPaymentDisabled} onOrder={handleOrder} editingProduct={editingProduct ? {
      product: editingProduct.product,
      allOptions
    } : null} onEditClose={() => setEditingProduct(null)} onEditConfirm={handleOptionConfirm} />

      {/* Desktop Option Change Modal */}
      {isDesktop && editingProduct && <CartOptionChangeModal opened={!!editingProduct} onClose={() => setEditingProduct(null)} product={editingProduct.product} allOptions={allOptions} onConfirm={handleOptionConfirm} />}

      {/* 삭제 확인 모달 */}
      <Modal opened={!!removeTarget} onClose={() => setRemoveTarget(null)} closeButton header={<p> </p>} buttonFooter={<>
            <Button theme="primary-fill" dimensions={isDesktop ? 'H56' : 'H48'} className="whitespace-nowrap" onClick={() => {
        confirmRemoveProduct();
        toast({
          message: '상품이 삭제되었습니다'
        });
      }}>
              삭제
            </Button>
            <Button theme="sub-fill" dimensions={isDesktop ? 'H56' : 'H48'} className="whitespace-nowrap" onClick={() => setRemoveTarget(null)}>
              취소
            </Button>
          </>}>
        <div className="max-w-[26rem] pt-12">
          <p className={cn('pb-16 font-800 text-Gray-900 typo-h5', 'mobile-tablet:pb-8 mobile-tablet:font-700 mobile-tablet:typo-body2')}>
            상품 삭제
          </p>
          <p className={cn('font-400 text-Gray-700 typo-body2', 'mobile-tablet:typo-body3')}>
            선택한 상품을 삭제하시겠습니까?
          </p>
        </div>
      </Modal>
    </>;
};